import { Link, createFileRoute } from '@tanstack/react-router';
import { TrendingUp } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Card } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { EMPTY_ACTION_CLASS } from '@/components/emptyStateClasses';
import { HabitsCard } from '@/components/habits/HabitsCard';
import { ErrorBanner } from '@/components/ErrorBanner';
import { PageHeader } from '@/components/PageHeader';
import { Spinner } from '@/components/Spinner';
import { BodyMetricHistory } from '@/components/progress/BodyMetricHistory';
import { MuscleVolumeChart } from '@/components/progress/MuscleVolumeChart';
import { PrList } from '@/components/progress/PrList';
import { RangeTabs, type RangeDays } from '@/components/progress/RangeTabs';
import { StatGrid } from '@/components/progress/StatGrid';
import { TrendChart, type TrendPoint } from '@/components/progress/TrendChart';
import { formatDate, formatNumber } from '@/components/workout/format';
import { BODY_METRICS as t } from '@/lib/i18n/progress';
import { lastDays, useProgressSummary } from '@/lib/queries/progress';

export const Route = createFileRoute('/_app/progress/')({
  component: ProgressPage,
});

function ProgressPage() {
  const [days, setDays] = useState<RangeDays>(30);
  const range = useMemo(() => lastDays(days), [days]);
  const summary = useProgressSummary(range);

  const weightPoints = useMemo<TrendPoint[]>(
    () =>
      (summary.data?.weightTrend ?? []).map((point) => ({
        label: formatDate(point.date),
        value: point.weightKg,
      })),
    [summary.data],
  );

  return (
    <>
      <PageHeader title="Progreso" subtitle="Volumen, marcas y peso a lo largo del tiempo." />

      <div className="flex flex-col gap-4">
        <HabitsCard />
        <div data-tour="progress-range">
          <RangeTabs value={days} onChange={setDays} />
        </div>

        {summary.isPending ? (
          <div className="flex justify-center py-10">
            <Spinner size="lg" />
          </div>
        ) : summary.isError ? (
          <ErrorBanner
            title="No hemos podido cargar tu progreso"
            error={summary.error}
            onRetry={() => void summary.refetch()}
          />
        ) : (
          <>
            {summary.data.sessionsCount === 0 ? (
              // Sin entrenamientos en el periodo, cuatro ceros y dos gráficas
              // vacías no dicen nada: mejor decir de dónde salen los datos.
              <EmptyState
                icon={TrendingUp}
                title={`Sin entrenamientos en los últimos ${days} días`}
                description="Termina un entreno con sus series y aquí verás cuánto has entrenado, el volumen por músculo y tus marcas personales."
                action={
                  <Link to="/train" className={EMPTY_ACTION_CLASS}>
                    Ir a Entrenar
                  </Link>
                }
              />
            ) : (
              <StatGrid summary={summary.data} />
            )}

            {/* Escritorio: gráficas de dos en dos; `min-w-0` deja encoger a
                las de recharts cuando la ventana se estrecha. */}
            <div
              data-tour="progress-charts"
              className="flex flex-col gap-4 lg:grid lg:grid-cols-2 lg:items-start lg:*:min-w-0"
            >
              {summary.data.sessionsCount === 0 ? null : (
                <>
                  <Card
                    title="Volumen por músculo"
                    description="Suma de peso × repeticiones de cada grupo muscular en el periodo."
                  >
                    <MuscleVolumeChart data={summary.data.volumeByMuscle} />
                  </Card>

                  <Card
                    title="Marcas personales"
                    description="Tu mejor 1RM estimado (el peso que podrías levantar una vez) del periodo."
                  >
                    <PrList records={summary.data.prs} />
                  </Card>
                </>
              )}

              <Card
                className="lg:col-span-2"
                title="Peso corporal"
                description={t.weightDescription}
              >
                <TrendChart
                  data={weightPoints}
                  name="Peso"
                  format={(value) => `${formatNumber(value)} kg`}
                  emptyMessage={t.emptyTrend}
                />
              </Card>
            </div>
          </>
        )}
        <BodyMetricHistory range={range} />
      </div>
    </>
  );
}
